import type { LocationMetricInfo, LocationSelectionSource } from 'owa-calendar-helpers-types';
import type EnhancedLocation from 'owa-service/lib/contract/EnhancedLocation';
import { isRoom, LocationPersonaControlViewState } from 'owa-location-persona';
import type PersonaPostalAddress from 'owa-service/lib/contract/PersonaPostalAddress';

export function createLocationMetricsFromPersonas(
    locationPersonas: LocationPersonaControlViewState[]
): { [index: string]: LocationMetricInfo } {
    let metrics: { [index: string]: LocationMetricInfo } = {};
    locationPersonas.forEach(persona => {
        const relevanceData = persona.relevanceData;
        // For Rooms added from the the Location well or room picker could personaId can be a GUID,
        // However when saving events from the scheduling assistant, all the rooms in the Event Location collection are wiped out
        // and replaced by locations with email Id. So we need to record the rooms using emails to be able to reconcile the records
        const locationId = getLocationMetricId(persona.personaId, persona.postalAddress);
        if (locationId) {
            metrics[locationId] = {
                selectionSource: persona.selectionSource ?? null,
                TotalLength: persona.displayName.length,
                TotalDigits: persona.displayName.replace(new RegExp('[^\\d]', 'g'), '').length,
                locationSource: persona.postalAddress?.LocationSource
                    ? persona.postalAddress.LocationSource
                    : 'None',
                locationEntityType: persona.entityType,
                relevanceHint: relevanceData?.relevanceHint,
                locationIndex: relevanceData?.locationIndex,
                queryLength:
                    relevanceData &&
                    (relevanceData.queryString ? relevanceData.queryString.length : 0),
                isOfficeLocation: isOfficeLocation(persona.displayName),
                isUrlLocation: isUrlLocation(persona.displayName),
            };
        }
    });

    return metrics;
}

export function createLocationMetricsFromRoomInfo(
    roomName: string,
    roomEmail: string,
    selectionSource: LocationSelectionSource
): { [index: string]: LocationMetricInfo } {
    const locationMetrics: { [index: string]: LocationMetricInfo } = {};
    locationMetrics[roomEmail] = {
        selectionSource: selectionSource,
        TotalLength: roomName.length,
        locationSource: 'Resource',
    };

    return locationMetrics;
}

export function createLocationMetricsFromEnhancedLocation(
    location: EnhancedLocation,
    selectionSource: LocationSelectionSource
): LocationMetricInfo {
    const metrics: LocationMetricInfo = {
        selectionSource: selectionSource,
        TotalLength: location.DisplayName?.length,
        TotalDigits: location.DisplayName?.replace(new RegExp('[^\\d]', 'g'), '').length,
        locationSource: location.PostalAddress?.LocationSource
            ? location.PostalAddress.LocationSource
            : 'None',
        isOfficeLocation: location.DisplayName ? isOfficeLocation(location.DisplayName) : undefined,
        isUrlLocation: location.DisplayName ? isUrlLocation(location.DisplayName) : undefined,
    };

    return metrics;
}

export function getLocationMetricId(
    locationId: string | undefined,
    postalAddress: PersonaPostalAddress
) {
    return isRoom(postalAddress) ? postalAddress.LocationUri : locationId;
}

function isOfficeLocation(displayName: string) {
    return displayName.toLowerCase().indexOf('office') != -1;
}

function isUrlLocation(displayName: string) {
    return displayName.toLowerCase().indexOf('http') == 0;
}
